import React, { useEffect, useState, useRef } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { createCategory, getCategoriesPaged } from "../../services/categoryServices";
import { FaPlus } from "react-icons/fa";
import { FiSearch } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import "./style.css";
import LoadingScreen from "src/Loading/LoadingScreen";
import { toast } from "react-toastify";
import LoadingButton from "../../components/Buttons/LoadingButton";

interface Category {
  id: number;
  en_name: string;
  ar_name: string;
}

export default function CategoryTable() {
  const { t } = useTranslation();
  const [categories, setCategories] = useState<Category[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [globalLoading, setGlobalLoading] = useState<boolean>(false);
  const [tableLoading, setTableLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [form, setForm] = useState({ en_name: "", ar_name: "" });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const didSkipFirstSearch = useRef<boolean>(false);
  const [creating, setCreating] = useState<boolean>(false);

  const fetchPage = async (query: string = "", pageNo: number = 1, mode: "global" | "table" = "table") => {
    mode === "global" ? setGlobalLoading(true) : setTableLoading(true);
    try {
      const apiPageNo = Math.max(pageNo - 1, 0);
      const result = await getCategoriesPaged(query, pageSize, apiPageNo);
      setCategories(result.data || []);
      setTotalPages(result.totalPages || 1);
    } catch (error) {
      console.error(error);
    } finally {
      mode === "global" ? setGlobalLoading(false) : setTableLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleAddCategory = async () => {
    try {
      setCreating(true);
      await createCategory(form);
      setForm({ en_name: "", ar_name: "" });
      setShowModal(false);
      toast.success(t("create category successful!"));
      fetchPage(searchTerm, page, "table");
    } catch (error: unknown) {
      console.error("create category error:", error);
      const msg = error instanceof Error ? error.message : t("An error occurred during create category");
      toast.error(msg || t("An error occurred during create category"));
    } finally {
      setCreating(false);
    }
  };

  useEffect(() => {
    fetchPage("", 1, "global");
    didSkipFirstSearch.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (didSkipFirstSearch.current) {
      didSkipFirstSearch.current = false;
      return;
    }
    const timer = setTimeout(() => {
      const q = (searchTerm || "").trim();
      setPage(1);
      fetchPage(q, 1, "table");
    }, 1000);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  if (globalLoading) {
    return (
      <div className="loading-screen" style={{ backgroundColor: "rgba(255, 255, 255, 0.8)" }}>
        <div>
          <LoadingScreen />
        </div>
      </div>
    );
  }

  const totalLabel = Math.max(totalPages || 1, 1);

  return (
    <div className="root cat-sup-page">
      <header className="cat-sup-header">
        <h1 className="cat-sup-title">{t("category.Categories")}</h1>
        <p className="cat-sup-subtitle">{t("category.page_subtitle")}</p>
      </header>

      <div className="cat-sup-control-card">
        <div className="cat-sup-toolbar">
          <div className="cat-sup-search-wrap">
            <FiSearch className="cat-sup-search-icon" size={18} aria-hidden />
            <input
              type="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  const q = (searchTerm || "").trim();
                  setPage(1);
                  fetchPage(q, 1, "table");
                }
              }}
              placeholder={String(t("header.Search ...."))}
              className="cat-sup-search-input"
              aria-label={String(t("header.Search ...."))}
            />
          </div>
          <button type="button" className="cat-sup-btn cat-sup-btn-add" onClick={() => setShowModal(true)}>
            <FaPlus size={16} aria-hidden />
            <span>{t("category.Add Category")}</span>
          </button>
        </div>
      </div>

      <div className="cat-sup-table-panel">
        <div className="table-responsive">
          <table className="cat-sup-table">
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">{t("category.English Name")}</th>
                <th scope="col">{t("category.Arabic Name")}</th>
              </tr>
            </thead>
            <tbody>
              {tableLoading ? (
                <tr>
                  <td colSpan={3}>
                    <div className="cat-sup-table-loading">
                      <div className="spinner-border cat-sup-spinner" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : categories.length === 0 ? (
                <tr>
                  <td colSpan={3} className="cat-sup-empty-cell">
                    {t("category.no_categories")}
                  </td>
                </tr>
              ) : (
                categories.map((cat, index) => (
                  <tr key={cat.id}>
                    <td className="cat-sup-td-index">{(page - 1) * pageSize + index + 1}</td>
                    <td className="cat-sup-td-name">{cat.en_name}</td>
                    <td className="cat-sup-td-name" dir="auto">
                      {cat.ar_name}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <nav className="cat-sup-pagination" aria-label={String(t("surveys.page"))}>
        <button
          type="button"
          className="cat-sup-page-btn"
          onClick={() => {
            setPage(1);
            fetchPage(searchTerm, 1, "table");
          }}
          disabled={page === 1}
        >
          «
        </button>
        <button
          type="button"
          className="cat-sup-page-btn"
          onClick={() => {
            const p = Math.max(page - 1, 1);
            setPage(p);
            fetchPage(searchTerm, p, "table");
          }}
          disabled={page === 1}
        >
          ‹ {t("institution.pagination_previous")}
        </button>
        <span className="cat-sup-page-current" aria-current="page">
          {page}
        </span>
        <button
          type="button"
          className="cat-sup-page-btn"
          onClick={() => {
            const p = Math.min(page + 1, totalLabel);
            setPage(p);
            fetchPage(searchTerm, p, "table");
          }}
          disabled={page >= totalLabel}
        >
          {t("institution.pagination_next")} ›
        </button>
        <button
          type="button"
          className="cat-sup-page-btn"
          onClick={() => {
            const p = totalLabel;
            setPage(p);
            fetchPage(searchTerm, p, "table");
          }}
          disabled={page >= totalLabel}
        >
          »
        </button>
        <span className="cat-sup-page-meta">
          {t("surveys.page")} {page} {t("surveys.to")} {totalLabel}
        </span>
      </nav>

      <Modal
        isOpen={showModal}
        toggle={() => setShowModal(false)}
        className="cat-sup-modal"
        contentClassName="cat-sup-modal-content"
        backdropClassName="cat-sup-modal-backdrop"
        centered
      >
        <ModalHeader
          toggle={() => setShowModal(false)}
          className="cat-sup-modal-header cat-sup-modal-header--brand"
          close={
            <button
              type="button"
              className="cat-sup-modal-close"
              onClick={() => setShowModal(false)}
              aria-label={String(t("category.Cancel") || "Close")}
            >
              ×
            </button>
          }
        >
          {t("category.Add Category")}
        </ModalHeader>
        <ModalBody className="cat-sup-modal-body">
          <p className="cat-sup-modal-hint">{t("category.modal_hint")}</p>
          <div className="cat-sup-field">
            <label className="cat-sup-label" htmlFor="cat-en-name">
              {t("category.English Name")}
            </label>
            <input
              id="cat-en-name"
              type="text"
              name="en_name"
              value={form.en_name}
              onChange={handleInputChange}
              className="form-control cat-sup-input"
              placeholder="Enter English Name"
              autoComplete="off"
            />
          </div>
          <div className="cat-sup-field cat-sup-field--last">
            <label className="cat-sup-label" htmlFor="cat-ar-name">
              {t("category.Arabic Name")}
            </label>
            <input
              id="cat-ar-name"
              type="text"
              name="ar_name"
              value={form.ar_name}
              onChange={handleInputChange}
              className="form-control cat-sup-input"
              placeholder="ادخل الاسم بالعربية"
              dir="rtl"
              autoComplete="off"
            />
          </div>
        </ModalBody>
        <ModalFooter className="cat-sup-modal-footer">
          <button type="button" className="cat-sup-btn cat-sup-btn-muted" onClick={() => setShowModal(false)}>
            {String(t("category.Cancel") || "Cancel")}
          </button>
          <LoadingButton
            onClick={handleAddCategory}
            variant="success"
            className="cat-sup-btn cat-sup-btn-modal-save"
            disabled={!form.en_name || !form.ar_name || creating}
            loadingText={String(t("category.Saving...") || "Saving...")}
          >
            {String(t("category.Save") || "Save")}
          </LoadingButton>
        </ModalFooter>
      </Modal>
    </div>
  );
}
